import { createTheme, type MantineColorsTuple } from '@mantine/core';

// Purple palette — calm, low-saturation, trader-friendly
const brand: MantineColorsTuple = [
    '#f3f2ff',
    '#e5e3ff',
    '#ccc9ff',
    '#b0abff',
    '#9991f5',
    '#8a80f0',
    '#7c72ec',
    '#6f65e8',
    '#6058d4', // primary accent light (matches --app-accent)
    '#524bbf',
];

export const theme = createTheme({
    primaryColor: 'brand',
    primaryShade: {
        light: 8,
        dark: 8,
    },
    colors: {
        brand,
    },
    defaultRadius: 'md',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    headings: {
        fontFamily: 'IBM Plex Sans, Inter, system-ui, sans-serif',
    },
    components: {
        Button: {
            defaultProps: {
                radius: 'md',
                size: 'xs',
            },
        },
        Input: {
            defaultProps: {
                radius: 'md',
                size: 'xs',
            },
            styles: {
                wrapper: {
                    '--app-current-field-bg': 'var(--mantine-color-default)',
                    '--app-current-field-border': 'var(--app-border)',
                    borderRadius: 'var(--input-radius)',
                    boxShadow: 'none',
                    transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                    '&:not(:focusWithin):hover': {
                        '--app-current-field-bg': 'var(--app-field-hover-bg)',
                        '--app-current-field-border': 'var(--app-border-strong)',
                    },
                    '&:focusWithin': {
                        '--app-current-field-bg': 'var(--app-field-focus-bg)',
                        '--app-current-field-border': 'var(--app-field-focus-border)',
                        boxShadow: 'var(--app-field-focus-shadow)',
                    },
                },
                input: {
                    background: 'var(--app-current-field-bg)',
                    borderColor: 'var(--app-current-field-border)',
                    color: 'var(--mantine-color-text)',
                    transition: 'border-color 160ms ease, background-color 160ms ease',
                    '&::placeholder': {
                        color: 'var(--mantine-color-dimmed)',
                    },
                    '&:hover': {
                        background: 'var(--app-field-hover-bg)',
                        borderColor: 'var(--app-border-strong)',
                    },
                    '&:focus:hover, &:focusWithin:hover': {
                        background: 'var(--app-current-field-bg)',
                        borderColor: 'var(--app-current-field-border)',
                    },
                    '&:focus, &:focusWithin': {
                        background: 'var(--app-current-field-bg)',
                        borderColor: 'var(--app-current-field-border)',
                        boxShadow: 'none',
                    },
                },
                section: {
                    background: 'var(--app-current-field-bg)',
                    color: 'var(--mantine-color-dimmed)',
                    transition: 'color 160ms ease, background-color 160ms ease',
                },
            },
        },
        TextInput: {
            defaultProps: { size: 'xs' },
        },
        NumberInput: {
            defaultProps: { size: 'xs' },
        },
        PasswordInput: {
            defaultProps: { size: 'xs' },
        },
        Anchor: {
            styles: {
                root: {
                    color: 'var(--app-link)',
                    fontWeight: 500,
                    textDecoration: 'none',
                    textUnderlineOffset: '0.18em',
                    transition: 'color 140ms ease, text-decoration-color 140ms ease, box-shadow 140ms ease',
                    '&:hover': {
                        color: 'var(--app-link-hover)',
                        textDecoration: 'underline',
                        textDecorationColor: 'currentColor',
                    },
                    '&:focusVisible': {
                        borderRadius: '6px',
                        boxShadow: 'var(--app-link-focus-shadow)',
                        outline: 'none',
                    },
                },
            },
        },
        InputWrapper: {
            styles: {
                label: {
                    color: 'var(--mantine-color-text)',
                    fontWeight: 600,
                },
                description: {
                    color: 'var(--mantine-color-dimmed)',
                },
            },
        },
        Select: {
            defaultProps: {
                checkIconPosition: 'right',
                size: 'xs',
            },
            styles: {
                dropdown: {
                    background: 'var(--mantine-color-default)',
                    border: '1px solid var(--app-border)',
                    boxShadow: 'var(--box-shadow)',
                },
                option: {
                    '&[data-combobox-active]': {
                        background: 'var(--mantine-color-default-hover)',
                    },
                    '&[data-combobox-selected]': {
                        background: 'var(--app-accent-soft)',
                        color: 'var(--mantine-color-text)',
                    },
                },
                empty: {
                    color: 'var(--mantine-color-dimmed)',
                },
            },
        },
        MultiSelect: {
            defaultProps: {
                size: 'xs',
            },
            styles: {
                dropdown: {
                    background: 'var(--mantine-color-default)',
                    border: '1px solid var(--app-border)',
                    boxShadow: 'var(--box-shadow)',
                },
                option: {
                    '&[data-combobox-active]': {
                        background: 'var(--mantine-color-default-hover)',
                    },
                    '&[data-combobox-selected]': {
                        background: 'var(--app-accent-soft)',
                        color: 'var(--mantine-color-text)',
                    },
                },
                pill: {
                    background: 'var(--mantine-color-default-hover)',
                    color: 'var(--mantine-color-text)',
                },
                empty: {
                    color: 'var(--mantine-color-dimmed)',
                },
            },
        },
        Card: {
            defaultProps: {
                withBorder: true,
                radius: 'lg',
                shadow: 'xs',
            },
            styles: {
                root: {
                    background: 'var(--mantine-color-default)',
                    borderColor: 'var(--app-border)',
                    boxShadow: 'var(--box-shadow)',
                },
            },
        },
        Paper: {
            defaultProps: {
                withBorder: true,
                radius: 'lg',
            },
            styles: {
                root: {
                    background: 'var(--mantine-color-default)',
                    borderColor: 'var(--app-border)',
                    boxShadow: 'var(--box-shadow)',
                },
            },
        },
        Modal: {
            defaultProps: {
                radius: 'lg',
            },
            styles: {
                content: {
                    background: 'var(--mantine-color-default)',
                },
                header: {
                    background: 'var(--mantine-color-default)',
                },
                body: {
                    background: 'var(--mantine-color-default)',
                },
            },
        },
        Accordion: {
            defaultProps: {
                radius: 'lg',
                variant: 'separated',
            },
            styles: {
                root: {
                    gap: '8px',
                },
                item: {
                    margin: 8,
                    border: '1px solid var(--app-border)',
                    background: 'var(--mantine-color-default)',
                    boxShadow: 'var(--box-shadow)',
                    overflow: 'hidden',
                    transition: 'background-color 180ms ease',
                    '&[dataActive]': {
                        background: 'var(--mantine-color-default-hover)',
                    },
                },
                chevron: {
                    color: 'var(--app-accent)',
                },
            },
        },
        Badge: {
            defaultProps: {
                radius: 'lg',
                variant: 'light',
            },
            styles: {
                root: {
                    fontWeight: 600,
                },
            },
        },
        Pagination: {
            defaultProps: {
                radius: 'md',
            },
            styles: {
                control: {
                    background: 'var(--mantine-color-default)',
                    color: 'var(--mantine-color-text)',
                    transition: 'border-color 160ms ease, background-color 160ms ease, color 160ms ease',
                    '&:hover': {
                        borderColor: 'var(--app-border-strong)',
                        background: 'var(--mantine-color-default-hover)',
                    },
                    '&[dataActive]': {
                        background: 'var(--app-accent-soft)',
                        color: 'var(--mantine-color-text)',
                    },
                },
                dots: {
                    color: 'var(--mantine-color-dimmed)',
                },
            },
        },
        Tabs: {
            defaultProps: {
                radius: 'md',
            },
            styles: {
                list: {
                    borderColor: 'var(--app-border)',
                },
                tab: {
                    color: 'var(--app-text-secondary)',
                    transition: 'color 160ms ease, background-color 160ms ease, border-color 160ms ease',
                    '&:hover': {
                        background: 'var(--mantine-color-default-hover)',
                        color: 'var(--mantine-color-text)',
                    },
                    '&[dataActive]': {
                        color: 'var(--mantine-color-text)',
                        background: 'var(--app-accent-soft)',
                    },
                },
                panel: {
                    paddingTop: '0.75rem',
                },
            },
        },
        Menu: {
            defaultProps: {
                radius: 'lg',
            },
            styles: {
                dropdown: {
                    background: 'var(--mantine-color-default)',
                    border: '1px solid var(--app-border)',
                    boxShadow: 'var(--box-shadow)',
                },
                item: {
                    color: 'var(--mantine-color-text)',
                    borderRadius: '12px',
                    transition: 'background-color 140ms ease, color 140ms ease',
                    '&:hover, &[data-hovered]': {
                        background: 'var(--mantine-color-default-hover)',
                        color: 'var(--mantine-color-text)',
                    },
                    '&[data-selected]': {
                        background: 'var(--app-accent-soft)',
                        color: 'var(--mantine-color-text)',
                    },
                },
                itemSection: {
                    color: 'var(--mantine-color-dimmed)',
                },
                divider: {
                    borderColor: 'var(--app-border)',
                },
                label: {
                    color: 'var(--mantine-color-dimmed)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                },
                arrow: {
                    background: 'var(--mantine-color-default)',
                    borderColor: 'var(--app-border)',
                },
            },
        },
        Notification: {
            defaultProps: {
                withBorder: false,
                radius: 'lg',
            },
            styles: {
                root: {
                    background: 'var(--mantine-color-default)',
                    color: 'var(--mantine-color-text)',
                    border: '1px solid var(--app-border)',
                    boxShadow: 'var(--box-shadow)',
                },
                title: {
                    color: 'var(--mantine-color-text)',
                    fontWeight: 700,
                },
                description: {
                    color: 'var(--mantine-color-dimmed)',
                },
                closeButton: {
                    color: 'var(--mantine-color-dimmed)',
                    '&:hover': {
                        background: 'var(--app-accent-soft)',
                        color: 'var(--mantine-color-text)',
                    },
                },
            },
        },
        Table: {
            styles: {
                th: {
                    color: 'var(--mantine-color-text)',
                    borderColor: 'var(--app-border)',
                    fontWeight: 700,
                },
                td: {
                    borderColor: 'var(--app-border)',
                    color: 'var(--mantine-color-text)',
                },
            },
        },
    },
});
